import {
  addColumnsToTable,
  AddNewColumnsPayload,
  AddOrRemovePapersToTablePayload,
  addPapersToTable,
  cacheDataForThread,
  editTableCell as editTableCellInStore,
  getTableStateForThread,
  removeColumnsFromTable,
  RemoveColumnsFromTablePayload,
  removeRowsFromTable,
  RemoveRowsFromTablePayload,
  setLoadingTableCells,
  updateTableColumn as updateTableColumnInStore,
  UpdateTableColumnPayload,
  updateTableRow as updateTableRowInStore,
  UpdateTableRowPayload,
} from '@/store/reducers/TableWidgetStore';
import { addError } from '@/store/reducers/ToastStore';
import { fetchTableStateByWidgetId } from '@/store/thunks/FetchTable';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { getTableAPI } from '@/api/TableApi';
import {
  getUpdatedTableResponseFromApi,
  TableColumn,
  tableColumnFactory,
  TableRow,
} from '@/models/Table';
import { RootState } from '@/store/store';
import { ThreadId } from '@/models/Thread';

import { createAsyncThunk } from '@reduxjs/toolkit';

type ReorderTableRowsPayload = {
  threadId: ThreadId;
  rows: TableRow[];
};

type ReorderTableColumnsPayload = {
  threadId: ThreadId;
  columns: TableColumn[];
};

export const addTableRows = createAsyncThunk<
  void,
  AddOrRemovePapersToTablePayload,
  { state: RootState }
>(
  'data/addTableRows',
  async ({ currentThreadId, papers }, { dispatch, getState }) => {
    // optimistically add papers to table
    dispatch(addPapersToTable({ currentThreadId, papers }));

    // add row in the backend
    try {
      const tableApi = getTableAPI();
      const state = getState();
      const tableData = getTableStateForThread(state, currentThreadId);
      let tableId = tableData && 'id' in tableData ? tableData?.id : null;

      if (!tableId) {
        const newTableRes = await tableApi.createTable(currentThreadId);
        tableId = newTableRes.data?.table_id;
      }

      if (!tableId) {
        throw Error('Table ID not found');
      }

      const updatedResp = await tableApi.addPapersToTable({
        tableId: tableId,
        threadId: currentThreadId,
        paperCorpusIds: papers.map((paper) => paper.corpusId),
      });

      const typedUpdatedTableResp = getUpdatedTableResponseFromApi(
        updatedResp.data,
      );

      // fetch the table state to update the UI with the new row and populated cells
      dispatch(
        fetchTableStateByWidgetId({
          threadId: currentThreadId,
          widgetId: typedUpdatedTableResp.tableId,
        }),
      );
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error adding rows', e);
      dispatch(
        addError({
          text: `Error adding ${
            papers.length > 1 ? 'rows' : 'row'
          }. Please try again.`,
        }),
      );
    }
  },
);

export const addTableColumns = createAsyncThunk<
  void,
  AddNewColumnsPayload,
  { state: RootState }
>(
  'data/addTableColumns',
  async ({ currentThreadId, columns }, { dispatch, getState }) => {
    // optimistically add column to table
    const optimisticColumns = columns.map((column) =>
      tableColumnFactory({
        name: column.name,
        description: column.description,
      }),
    );
    dispatch(
      addColumnsToTable({ currentThreadId, columns: optimisticColumns }),
    );

    // add column in the backend
    try {
      const tableApi = getTableAPI();
      const state = getState();
      const tableData = getTableStateForThread(state, currentThreadId);
      let tableId = tableData && 'id' in tableData ? tableData?.id : null;

      if (!tableId) {
        const newTableRes = await tableApi.createTable(currentThreadId);
        tableId = newTableRes.data?.table_id;
      }

      if (!tableId) {
        throw Error('Table ID not found');
      }

      const updatedResp = await tableApi.addColumnsToTable({
        tableId: tableId,
        threadId: currentThreadId,
        columns,
      });

      const typedUpdatedTableResp = getUpdatedTableResponseFromApi(
        updatedResp.data,
      );

      // fetch the table state to update the UI with the new column and populated cells
      dispatch(
        fetchTableStateByWidgetId({
          threadId: currentThreadId,
          widgetId: typedUpdatedTableResp.tableId,
        }),
      );
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error adding columns', e);
      dispatch(
        addError({
          text: `Error adding ${
            columns.length > 1 ? 'columns' : 'column'
          }. Please try again.`,
        }),
      );
    }
  },
);

export const removeTableRows = createAsyncThunk<
  void,
  RemoveRowsFromTablePayload,
  { state: RootState }
>(
  'data/removeTableRows',
  async ({ currentThreadId, rowIds }, { dispatch, getState }) => {
    const state = getState();
    const tableData = getTableStateForThread(state, currentThreadId);

    // optimistically remove rows from table
    dispatch(
      removeRowsFromTable({
        currentThreadId: currentThreadId,
        rowIds,
      }),
    );

    if (!tableData || !('id' in tableData)) {
      return;
    }

    // remove rows in the backend
    try {
      const tableApi = getTableAPI();
      tableApi.removeRowsFromTable({
        tableId: tableData.id,
        threadId: currentThreadId,
        rowIds,
      });
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error removing row', e);
      dispatch(
        addError({
          text: `Error removing row. Please try again.`,
        }),
      );
    }
  },
);

export const removeTableColumns = createAsyncThunk<
  void,
  RemoveColumnsFromTablePayload,
  { state: RootState }
>(
  'data/removeTableColumns',
  async ({ currentThreadId, columnIds }, { dispatch, getState }) => {
    const state = getState();
    const tableData = getTableStateForThread(state, currentThreadId);

    // optimistically remove columns from table
    dispatch(
      removeColumnsFromTable({
        currentThreadId: currentThreadId,
        columnIds,
      }),
    );

    if (!tableData || !('id' in tableData)) {
      return;
    }

    // remove columns in the backend
    try {
      const tableApi = getTableAPI();
      tableApi.removeColumnsFromTable({
        tableId: tableData.id,
        threadId: currentThreadId,
        columnIds,
      });
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error removing column', e);
      dispatch(
        addError({
          text: `Error removing column. Please try again.`,
        }),
      );
    }
  },
);

export const updateTableRow = createAsyncThunk<
  void,
  UpdateTableRowPayload,
  { state: RootState }
>(
  'data/updateTableRow',
  async ({ currentThreadId, row }, { dispatch, getState }) => {
    const state = getState();
    const tableData = getTableStateForThread(state, currentThreadId);

    if (!tableData || !('id' in tableData)) {
      return;
    }

    // optimistically update row
    dispatch(setLoadingTableCells({ threadId: currentThreadId, rows: [row] }));
    dispatch(
      updateTableRowInStore({
        currentThreadId: currentThreadId,
        row,
      }),
    );

    // update row in the backend
    try {
      const tableApi = getTableAPI();
      tableApi.updateRowInTable({
        tableId: tableData.id,
        threadId: currentThreadId,
        row,
      });
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error updating row', e);
      dispatch(
        addError({
          text: `Error updating row. Please try again.`,
        }),
      );
    }
  },
);

export const updateTableColumn = createAsyncThunk<
  void,
  UpdateTableColumnPayload,
  { state: RootState }
>(
  'data/updateTableColumn',
  async ({ currentThreadId, column }, { dispatch, getState }) => {
    const state = getState();
    const tableData = getTableStateForThread(state, currentThreadId);

    if (!tableData || !('id' in tableData)) {
      return;
    }

    // optimistically update column
    dispatch(
      setLoadingTableCells({ threadId: currentThreadId, columns: [column] }),
    );
    dispatch(
      updateTableColumnInStore({
        currentThreadId: currentThreadId,
        column,
      }),
    );

    // update column in the backend
    try {
      const tableApi = getTableAPI();
      await tableApi.updateColumnInTable({
        tableId: tableData.id,
        threadId: currentThreadId,
        column,
      });

      // fetch the table state to update the UI with the new column and populated cells
      dispatch(
        fetchTableStateByWidgetId({
          threadId: currentThreadId,
          widgetId: tableData.id,
        }),
      );
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error updating column', e);
      dispatch(
        addError({
          text: `Error updating column. Please try again.`,
        }),
      );
    }
  },
);

export const editTableCell = createAsyncThunk<
  void,
  {
    currentThreadId: ThreadId;
    cellKey: string;
    editedValue: string | null;
  },
  { state: RootState }
>(
  'data/editTableCell',
  async ({ currentThreadId, cellKey, editedValue }, { dispatch, getState }) => {
    const state = getState();
    const editedByUUID = state.userStore.currentUser?.uuid;

    const tableData = getTableStateForThread(state, currentThreadId);
    if (!tableData || !('id' in tableData)) {
      return;
    }

    dispatch(
      editTableCellInStore({
        currentThreadId: currentThreadId,
        cellId: cellKey,
        editedValue: editedValue,
        editedByUUID: editedByUUID || null,
      }),
    );

    // edit cells in the backend
    try {
      const tableApi = getTableAPI();
      await tableApi.editTableCell({
        tableId: tableData.id,
        cellId: cellKey,
        threadId: currentThreadId,
        editedValue: editedValue || '',
        editedByUUID: editedByUUID || '',
      });
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error editing table cell', e);
      dispatch(
        addError({
          text: `Error editing table cell. Please try again.`,
        }),
      );
    }

    // fetch the table state to update the UI with the populated cells
    // regardless of success or failure so it stops the loading state
    dispatch(
      fetchTableStateByWidgetId({
        threadId: currentThreadId,
        widgetId: tableData.id,
      }),
    );
  },
);

export const populateTableCells = createAsyncThunk<
  void,
  { threadId: ThreadId; columns: TableColumn[]; rows: TableRow[] },
  { state: RootState }
>(
  'data/populateTableCells',
  async ({ threadId, columns, rows }, { dispatch, getState }) => {
    const state = getState();
    const tableData = getTableStateForThread(state, threadId);
    if (!tableData || !('id' in tableData)) {
      return;
    }

    // optimistically set loading state for cells
    dispatch(
      setLoadingTableCells({
        threadId,
        columns,
        rows,
      }),
    );

    // populate cells in the backend
    try {
      const tableApi = getTableAPI();
      await tableApi.populateTableCells({
        tableId: tableData.id,
        threadId,
        columnIds: columns.map((col) => col.id),
        rowIds: rows.map((row) => row.id),
      });
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error populating cells', e);
      dispatch(
        addError({
          text: `Error populating cells. Please try again.`,
        }),
      );
    }

    // fetch the table state to update the UI with the populated cells
    // regardless of success or failure so it stops the loading state
    dispatch(
      fetchTableStateByWidgetId({
        threadId,
        widgetId: tableData.id,
      }),
    );
  },
);

export const reorderTableRows = createAsyncThunk<
  void,
  ReorderTableRowsPayload,
  { state: RootState }
>(
  'data/reorderTableRows',
  async ({ threadId, rows }, { dispatch, getState }) => {
    const state = getState();
    const tableData = getTableStateForThread(state, threadId);

    if (!tableData || !('id' in tableData)) {
      return;
    }

    // optimistically update in store
    dispatch(
      cacheDataForThread({ threadId, tableData: { ...tableData, rows } }),
    );

    try {
      // call API to persist reordered rows
      const tableApi = getTableAPI();
      await tableApi.reorderTableRows({
        threadId,
        tableId: tableData.id,
        rows,
      });
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error reordering table rows', e);
      dispatch(
        addError({
          text: `Error reordering table rows. Please try again.`,
        }),
      );
    }
  },
);

export const reorderTableColumns = createAsyncThunk<
  void,
  ReorderTableColumnsPayload,
  { state: RootState }
>(
  'data/reorderTableColumns',
  async ({ threadId, columns }, { dispatch, getState }) => {
    const state = getState();
    const tableData = getTableStateForThread(state, threadId);

    if (!tableData || !('id' in tableData)) {
      return;
    }

    // optimistically update in store
    dispatch(
      cacheDataForThread({ threadId, tableData: { ...tableData, columns } }),
    );

    try {
      // call API to persist reordered columns
      const tableApi = getTableAPI();
      await tableApi.reorderTableColumns({
        threadId,
        tableId: tableData.id,
        columns,
      });
    } catch (e: any) {
      // TODO: rollback the optimistic update
      getLayoverLogger().logError('Error reordering table columns', e);
      dispatch(
        addError({
          text: `Error reordering table columns. Please try again.`,
        }),
      );
    }
  },
);
